import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertCircle, ArrowUpRight, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/lib/supabase';

interface BusinessOpportunity {
  id: string;
  name: string;
  customer_id: string;
  start_date: string;
  opportunity_type: string;
  stage: string;
  status: string;
  close_probability: number;
  value: number;
  currency_code: string;
  created_at: string;
  customer?: {
    name: string;
  };
}

const OpportunityConfirmation = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opportunity, setOpportunity] = useState<BusinessOpportunity | null>(null);

  useEffect(() => {
    if (id) {
      fetchOpportunity(id);
    } else {
      setError('ID da oportunidade não fornecido');
      setLoading(false);
    }
  }, [id]);

  const fetchOpportunity = async (opportunityId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('business_opportunities')
        .select(`
          *,
          customer:customers(name)
        `)
        .eq('id', opportunityId)
        .single();

      if (error) throw error;
      setOpportunity(data);
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      setError('Não foi possível carregar os dados da oportunidade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-10">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-10">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Carregando informações da oportunidade...</p>
              </CardContent>
            </Card>
          ) : error ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-10">
                <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                <h2 className="text-xl font-extrabold mb-2">Ocorreu um erro</h2>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={() => navigate('/opportunities')}>
                  Voltar para Oportunidades
                </Button>
              </CardContent>
            </Card>
          ) : opportunity ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-10">
                <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
                <h2 className="text-2xl font-extrabold mb-2">Oportunidade Criada com Sucesso!</h2>
                <p className="text-muted-foreground text-center mb-2">
                  A oportunidade para {opportunity.customer?.name} foi criada com sucesso.
                </p>
                <p className="font-extrabold mb-6">
                  {opportunity.name}
                </p>
                
                <div className="grid grid-cols-2 gap-4 w-full mb-8">
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Cliente</div>
                    <div className="font-extrabold">{opportunity.customer?.name}</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Tipo</div>
                    <div className="font-extrabold">{opportunity.opportunity_type}</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Estágio</div>
                    <div className="font-extrabold">{opportunity.stage}</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Probabilidade</div>
                    <div className="font-extrabold">{opportunity.close_probability}%</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Valor</div>
                    <div className="font-extrabold">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: opportunity.currency_code,
                        minimumFractionDigits: 2
                      }).format(opportunity.value)}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/opportunity-wizard/step1')}
                  >
                    Nova Oportunidade
                  </Button>
                  <Button 
                    onClick={() => navigate(`/opportunities/${opportunity.id}`)}
                  >
                    <ArrowUpRight className="h-4 w-4 mr-2" />
                    Ver Detalhes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-10">
                <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                <h2 className="text-xl font-extrabold mb-2">Oportunidade não encontrada</h2>
                <p className="text-muted-foreground mb-6">Não foi possível encontrar os detalhes da oportunidade</p>
                <Button onClick={() => navigate('/opportunities')}>
                  Voltar para Oportunidades
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OpportunityConfirmation; 